import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { wikiApi } from '../../api/wiki.js';

/**
 * /wiki/spaces — grid of all accessible spaces.
 */
export default function Spaces() {
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState('all'); // all | mine | team | global
  const navigate = useNavigate();

  useEffect(() => {
    wikiApi.listSpaces()
      .then((d) => setSpaces(d?.items || []))
      .catch(() => setSpaces([]))
      .finally(() => setLoading(false));
  }, []);

  const visible = spaces.filter((s) => {
    if (filter === 'team') return s.type === 'team';
    if (filter === 'global') return s.type === 'global';
    if (filter === 'mine') return s.owner_id; // simplified
    return true;
  });

  return (
    <section className="wiki-spaces" data-testid="wiki-spaces" data-loaded={loading ? 'false' : 'true'}>
      <header className="page-header" style={{ padding: '20px 24px' }}>
        <div>
          <h1>📚 Wiki</h1>
          <p className="muted">Spaces are top-level containers for your team's documentation.</p>
        </div>
        <button className="btn primary" data-testid="create-space-btn" onClick={() => setCreating(true)}>+ Create space</button>
      </header>

      <div style={{ padding: '0 24px 24px' }}>
        <div style={{ marginBottom: 16, display: 'flex', gap: 6 }}>
          {[
            { id: 'all',    label: 'All spaces' },
            { id: 'mine',   label: 'My spaces' },
            { id: 'team',   label: 'Team' },
            { id: 'global', label: 'Global' },
          ].map((f) => (
            <button
              key={f.id}
              className={'btn ghost small ' + (filter === f.id ? 'is-active' : '')}
              style={{ background: filter === f.id ? '#e0e7ff' : 'transparent' }}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading && <div className="muted">Loading spaces…</div>}
        {!loading && visible.length === 0 && (
          <div className="card" style={{ textAlign: 'center', padding: 48 }}>
            <p className="muted">No spaces yet. Click "Create space" to get started.</p>
          </div>
        )}

        <div className="card-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {visible.map((s) => (
            <Link
              key={s.id}
              to={`/wiki/${s.key}`}
              className="card"
              data-testid="space-card"
              data-space-key={s.key}
              style={{ padding: 16, textDecoration: 'none', color: 'inherit', borderTop: `4px solid ${s.color}` }}
            >
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ fontSize: 32 }}>{s.icon}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <strong style={{ fontSize: 15 }}>{s.name}</strong>
                    <code style={{ background: '#e2e8f0', padding: '1px 6px', borderRadius: 3, fontSize: 11 }}>{s.key}</code>
                    {s.is_public && <span className="chip" style={{ fontSize: 10 }}>public</span>}
                  </div>
                  <p className="muted small" style={{ margin: '4px 0 8px', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    {s.description || 'No description.'}
                  </p>
                  <div style={{ fontSize: 11, color: '#64748b', display: 'flex', gap: 8 }}>
                    <span>{s.page_count || 0} {s.page_count === 1 ? 'page' : 'pages'}</span>
                    {s.last_activity && <span>· last edit {timeAgo(s.last_activity)}</span>}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {creating && (
        <CreateSpaceModal
          onClose={() => setCreating(false)}
          onCreated={(s) => { setCreating(false); navigate(`/wiki/${s.key}`); }}
        />
      )}
    </section>
  );
}

function CreateSpaceModal({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [icon, setIcon] = useState('📄');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  function autoKey(v) {
    setName(v);
    if (!key || key.startsWith(slug(name))) setKey(slug(v).toUpperCase().slice(0, 10));
  }
  function slug(s) { return String(s).toLowerCase().replace(/[^a-z]+/g, '').slice(0, 10); }

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const out = await wikiApi.createSpace({ name, key, icon, description });
      onCreated(out.space);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create space');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div role="dialog" aria-modal="true" aria-label="Create wiki space"
         style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
         onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
            className="card" style={{ width: 'min(90vw, 480px)', padding: 20 }}
            data-testid="create-space-modal">
        <h2 style={{ marginTop: 0 }}>Create space</h2>
        <label>Name<input value={name} onChange={(e) => autoKey(e.target.value)} required autoFocus /></label>
        <label>Key (max 10 chars, uppercase)<input value={key} onChange={(e) => setKey(e.target.value.toUpperCase().slice(0, 10))} required pattern="[A-Z0-9]+" /></label>
        <label>Icon<input value={icon} onChange={(e) => setIcon(e.target.value)} maxLength={4} style={{ width: 80 }} /></label>
        <label>Description<input value={description} onChange={(e) => setDescription(e.target.value)} /></label>
        {error && <div className="alert error" role="alert" style={{ marginTop: 8 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 12 }}>
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn primary" disabled={submitting || !name || !key}>
            {submitting ? 'Creating…' : 'Create space'}
          </button>
        </div>
      </form>
    </div>
  );
}

function timeAgo(iso) {
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}
